import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import API_BASE from '../../utils/apiBase';

// Manager-controlled HSG booking: one provider link, one CareDock save.
// No staff checkout, credential sharing or automatic course verification.
const COURSES = [
  {
    code: 'hsg_cstf_one_day_refresher',
    label: 'Refresher',
    title: 'CSTF One-Day Refresher',
    hint: 'Previously worked in care',
    href: 'https://www.healthandsafetygroup.com/p/mandatory-statutory-training-skills-for-health',
  },
  {
    code: 'hsg_care_certificate_initial',
    label: 'New starter',
    title: 'Care Certificate — New Starter',
    hint: 'New to care',
    href: 'https://www.healthandsafetygroup.com/p/care-certificate-training-course',
  },
];

export default function HsgAdminBookingCard({
  employeeId, token, requests = [], onRecorded,
}) {
  const [course, setCourse] = useState(COURSES[0].code);
  const [bookingStep, setBookingStep] = useState('choose');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [recordedLocally, setRecordedLocally] = useState({});
  const selected = COURSES.find(item => item.code === course);
  const booking = recordedLocally[course] || requests.find(req =>
    req.kind === 'training' && req.training_code === course && req.status === 'enrolled'
  );
  const anyBooking = Object.keys(recordedLocally).length > 0
    || requests.some(req => req.kind === 'training' && req.status === 'enrolled'
      && COURSES.some(item => item.code === req.training_code));

  const choose = code => {
    if (saving) return;
    setCourse(code);
    setBookingStep('choose');
    setFeedback('');
    setReference('');
  };

  const recordBooking = async (event) => {
    event.preventDefault();
    if (!employeeId || !token || saving || booking || reference.trim().length < 3) return;
    setSaving(true);
    setFeedback('');
    try {
      // One audited, tenant-scoped write. The server handles retries, existing
      // employee requests and conflicting booking references.
      const response = await axios.post(
        `${API_BASE}/employees/${employeeId}/training/hub/hsg-bookings`,
        { course_code: selected.code, booking_reference: reference.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data?.saved || response.data?.booking?.status !== 'enrolled') {
        throw new Error('Booking could not be confirmed in CareDock.');
      }
      setRecordedLocally(old => ({ ...old, [selected.code]: response.data.booking }));
      setReference('');
      setBookingStep('choose');
      setFeedback('Booking recorded. CareDock will continue tracking the certificate and checks.');
      if (onRecorded) await onRecorded();
    } catch (err) {
      setFeedback(err.response?.data?.detail || err.message
        || 'Could not save this booking. Please try again.');
      if (err.response?.status === 409 && onRecorded) await onRecorded();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
      data-testid="manager-hsg-training-pathways">
      <div>
        <h4 className="font-semibold text-slate-900">Arrange training</h4>
        <p className="text-xs text-slate-600">Choose the right course for this person. Your organisation handles the booking.</p>
      </div>
      {anyBooking && (
        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs text-teal-800" role="status">
          At least one HSG booking is recorded for this person. Check before paying again.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Choose staff training type">
        {COURSES.map(item => {
          const selectedItem = course === item.code;
          return (
            <button key={item.code} type="button" onClick={() => choose(item.code)}
              disabled={saving} aria-pressed={selectedItem}
              className={`rounded-xl border px-3 py-3 text-left min-h-16 ${selectedItem
                ? 'border-teal-700 bg-teal-50 text-teal-950'
                : 'border-slate-200 bg-slate-50 text-slate-800'}`}>
              <span className="block text-sm font-semibold">{item.label}</span>
              <span className="block text-xs">{item.hint}</span>
            </button>
          );
        })}
      </div>
      {booking ? (
        <div className="flex items-start gap-2 rounded-lg bg-teal-50 p-3 text-sm text-teal-900"
          data-testid="hsg-booking-recorded">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div><p className="font-semibold">Booking saved</p>
            <p className="text-xs">Reference: {booking.booking_reference}. Training and certificates still need checking.</p>
          </div>
        </div>
      ) : (
        <>
          <a href={selected.href} target="_blank" rel="noopener noreferrer"
            onClick={() => { setBookingStep('reference'); setFeedback(''); }}
            data-testid={`hsg-book-${selected.code}`}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white">
            Book {selected.title} with HSG <ExternalLink className="h-4 w-4" />
          </a>
          {bookingStep === 'choose' ? (
            <button type="button" className="text-xs text-teal-800 underline underline-offset-2"
              onClick={() => setBookingStep('reference')}>
              Already booked? Save the reference
            </button>
          ) : (
            <form onSubmit={recordBooking} className="space-y-2" data-testid="hsg-confirm-booking-form">
              <label className="block text-sm font-medium text-slate-800">
                HSG booking reference
                <input type="text" maxLength={120} value={reference}
                  disabled={saving} onChange={event => setReference(event.target.value)}
                  aria-label="HSG booking reference" autoComplete="off"
                  placeholder="Enter the actual booking reference"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-3 text-sm" />
              </label>
              <button type="submit" disabled={saving || !token || reference.trim().length < 3}
                className="min-h-11 w-full rounded-xl bg-teal-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {saving ? 'Saving…' : 'Save booking'}
              </button>
              <button type="button" className="text-xs text-slate-700 underline"
                onClick={() => setBookingStep('choose')} disabled={saving}>Back</button>
            </form>
          )}
        </>
      )}
      {feedback && <p role="status" className="text-xs text-slate-700">{feedback}</p>}
      <p className="text-xs text-slate-500">
        Training is arranged with HSG, not through CareDock. No certificate is approved by booking.
      </p>
    </section>
  );
}
